import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Label,
  Modal,
  Spinner,
  Textarea,
} from '@crm/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronRight, Pencil, Plus, Tags, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  ConfirmDialog,
  EmptyState,
  FormField,
  LOAD_MORE_STEP,
  LoadMore,
  PageHeader,
  PhotoField,
} from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useCan } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import {
  type Category,
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from '../api';

const FORM_ID = 'catalog-category-form';

const categoryFormSchema = z.object({
  name: z.string().min(2, 'Informe o nome').max(80),
  description: z.string().max(240),
  image: z.string().max(2048),
  isActive: z.boolean(),
});

type CategoryFormValues = z.infer<typeof categoryFormSchema>;

const EMPTY: CategoryFormValues = { name: '', description: '', image: '', isActive: true };

export function CategoriesPage() {
  useDocumentTitle('Categorias');

  const canWrite = useCan('services', 'WRITE');
  // Lista curta: "Ver mais" pede mais registros e eles entram abaixo dos que
  // ja estao na tela, sem separar paginas.
  const [perPage, setPerPage] = useState(LOAD_MORE_STEP);
  const categories = useCategories({ page: 1, perPage });
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();

  const [editing, setEditing] = useState<Category | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<CategoryFormValues>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: EMPTY,
  });

  const mutationError = createCategory.error ?? updateCategory.error ?? deleteCategory.error;

  function openForm(category: Category | null) {
    setEditing(category);
    setIsFormOpen(true);
    reset(
      category
        ? {
            name: category.name,
            description: category.description ?? '',
            image: category.image ?? '',
            isActive: category.isActive,
          }
        : EMPTY,
    );
  }

  function closeForm() {
    setIsFormOpen(false);
    setEditing(null);
    reset(EMPTY);
  }

  function onSubmit(values: CategoryFormValues) {
    if (editing) {
      updateCategory.mutate({ id: editing.id, input: values }, { onSuccess: closeForm });
      return;
    }

    createCategory.mutate(values, { onSuccess: closeForm });
  }

  const [pendingRemoval, setPendingRemoval] = useState<{ id: string; name: string } | null>(null);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Tags />}
        title="Categorias"
        description="Como os servicos sao agrupados no catalogo."
        actions={
          canWrite ? (
            <Button onClick={() => openForm(null)}>
              <Plus className="h-4 w-4" />
              <span>
                Nova<span className="hidden sm:inline"> categoria</span>
              </span>
            </Button>
          ) : null
        }
      />

      {mutationError ? (
        <p className="text-sm text-destructive">{toErrorMessage(mutationError)}</p>
      ) : null}

      <Modal
        open={isFormOpen}
        onClose={closeForm}
        title={editing ? 'Editar categoria' : 'Nova categoria'}
        actions={[
          { label: 'Cancelar', variant: 'ghost', onClick: closeForm },
          {
            label: editing ? 'Salvar' : 'Criar',
            type: 'submit',
            formId: FORM_ID,
            loading: createCategory.isPending || updateCategory.isPending,
          },
        ]}
      >
        <form id={FORM_ID} className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormField label="Nome" error={errors.name?.message} {...register('name')} />

          <div className="space-y-2">
            <Label htmlFor="description">Descricao</Label>
            <Textarea id="description" {...register('description')} />
          </div>

          <PhotoField
            label="Foto"
            shape="square"
            value={watch('image')}
            onChange={(valor) => setValue('image', valor, { shouldDirty: true })}
          />

          <Checkbox label="Categoria ativa" {...register('isActive')} />
        </form>
      </Modal>

      <Card variant="flush">
        <CardContent>
          {categories.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : categories.isError ? (
            <p className="text-sm text-destructive">{toErrorMessage(categories.error)}</p>
          ) : categories.data && categories.data.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {categories.data.data.map((category) => (
                <li key={category.id} className="flex items-center gap-2 py-3 first:pt-0 last:pb-0">
                  {/* A linha toda abre o cadastro: no celular o alvo e o
                      registro inteiro, nao um icone no canto. */}
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                    onClick={() => openForm(category)}
                  >
                    {category.image ? (
                      <img
                        src={category.image}
                        alt={category.name}
                        className="h-10 w-10 shrink-0 rounded-md border border-border object-cover"
                      />
                    ) : null}
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{category.name}</span>
                        <Badge variant="outline">
                          {category.servicesCount}{' '}
                          {category.servicesCount === 1 ? 'servico' : 'servicos'}
                        </Badge>
                        {category.isActive ? null : <Badge variant="destructive">Inativa</Badge>}
                      </span>
                      {category.description ? (
                        <span className="block truncate text-xs text-muted-foreground">
                          {category.description}
                        </span>
                      ) : null}
                    </span>
                  </button>

                  <ChevronRight
                    aria-hidden
                    className="h-5 w-5 shrink-0 text-muted-foreground sm:hidden"
                  />

                  {canWrite ? (
                    <div className="hidden shrink-0 items-center sm:flex">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Editar"
                        onClick={() => openForm(category)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Excluir"
                        disabled={deleteCategory.isPending}
                        onClick={() => setPendingRemoval({ id: category.id, name: category.name })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={<Tags />} title="Nenhuma categoria cadastrada" />
          )}

          <LoadMore
            meta={categories.data?.meta}
            label="categorias"
            loading={categories.isFetching}
            onLoadMore={() => setPerPage((current) => current + LOAD_MORE_STEP)}
          />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={pendingRemoval !== null}
        title="Excluir categoria"
        message={`A categoria ${pendingRemoval?.name ?? ''} sai do catalogo.`}
        loading={deleteCategory.isPending}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (pendingRemoval) deleteCategory.mutate(pendingRemoval.id);
          setPendingRemoval(null);
        }}
      />
    </div>
  );
}
